import React, { useState, useEffect, useRef } from 'react';
import { FileText, Upload, Trash2, CheckCircle, AlertCircle, Download, Eye, Settings, ShieldCheck, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ContractTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const fileInputRef = useRef(null);

  // Workmanship Warranty quick-send state
  const [showWarrantySendModal, setShowWarrantySendModal] = useState(false);
  const [warrantyForm, setWarrantyForm] = useState({
    customer_name: '',
    address: '',
    email: '',
    phone: '',
    project_completion_date: '',
  });
  const [isSendingWarranty, setIsSendingWarranty] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('rooftrack_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    loadTemplates();
    loadActiveTemplate();
  }, []);

  const getFilePreviewUrl = (template) => {
    if (!template?.file_url) return null;
    // If it's a relative URL (starts with /), prepend the backend URL
    if (template.file_url.startsWith('/')) {
      return `${BACKEND_URL}${template.file_url}`;
    }
    // Otherwise it's already a full URL (S3 presigned)
    return template.file_url;
  };

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contracts/templates/list`, {
        headers: getAuthHeaders()
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadActiveTemplate = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contracts/my-template`, {
        headers: getAuthHeaders()
      });
      if (response.data.has_custom_template) {
        setActiveTemplate(response.data.template);
      } else {
        setActiveTemplate(null);
      }
    } catch (error) {
      console.error('Error loading active template:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['.pdf', '.docx'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedTypes.includes(ext)) {
      alert('Please upload a PDF or DOCX file');
      return;
    }

    if (!templateName.trim()) {
      alert('Please enter a template name');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('template_name', templateName);
      formData.append('description', templateDescription);

      const response = await axios.post(
        `${BACKEND_URL}/api/contracts/upload-template`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      alert('Contract template uploaded successfully!');
      setShowUploadForm(false);
      setTemplateName('');
      setTemplateDescription('');
      loadTemplates();
      loadActiveTemplate();
    } catch (error) {
      console.error('Error uploading template:', error);
      alert(error.response?.data?.detail || 'Failed to upload template');
    } finally {
      setIsUploading(false);
    }
  };

  const handleActivate = async (templateId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/contracts/template/${templateId}/activate`,
        {},
        { headers: getAuthHeaders() }
      );
      alert('Template activated!');
      loadTemplates();
      loadActiveTemplate();
    } catch (error) {
      console.error('Error activating template:', error);
      alert('Failed to activate template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/contracts/template/${templateId}`, {
        headers: getAuthHeaders()
      });
      alert('Template deleted');
      loadTemplates();
      loadActiveTemplate();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    }
  };

  const handleUseDefault = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/contracts/use-default`,
        {},
        { headers: getAuthHeaders() }
      );
      alert('Now using the default HomeFixer360 contract template');
      loadTemplates();
      loadActiveTemplate();
    } catch (error) {
      console.error('Error switching to default:', error);
      alert('Failed to switch to default template');
    }
  };

  const handlePreviewWarranty = async () => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/warranty/preview`,
        {
          customer_name: warrantyForm.customer_name || 'Sample Customer',
          address: warrantyForm.address || '123 Example St, Anytown, USA',
          email: warrantyForm.email || 'sample@example.com',
          phone: warrantyForm.phone || '555-555-5555',
          project_completion_date: warrantyForm.project_completion_date || null,
        },
        { headers: getAuthHeaders(), responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error previewing warranty:', error);
      toast.error('Failed to preview warranty PDF');
    }
  };

  const handleSendWarranty = async () => {
    if (!warrantyForm.customer_name?.trim() || !warrantyForm.email?.trim()) {
      toast.error('Customer name and email are required');
      return;
    }
    setIsSendingWarranty(true);
    try {
      // Generate the PDF, then download it client-side AND email it.
      // We re-use the preview endpoint to get the bytes for client download,
      // and call a dedicated send endpoint for the email path.
      const sendRes = await axios.post(
        `${BACKEND_URL}/api/warranty/send/ad-hoc`,
        warrantyForm,
        { headers: getAuthHeaders() }
      );
      toast.success(
        `Warranty emailed to ${sendRes.data.customer_email}`
      );
      setShowWarrantySendModal(false);
      setWarrantyForm({
        customer_name: '',
        address: '',
        email: '',
        phone: '',
        project_completion_date: '',
      });
    } catch (error) {
      console.error('Error sending warranty:', error);
      toast.error(error.response?.data?.detail || 'Failed to send warranty');
    } finally {
      setIsSendingWarranty(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <FileText className="w-8 h-8 mr-3 text-blue-600" />
          Contract Templates
        </h1>
        <p className="text-gray-600 mt-2">
          Upload your own contract template or use the default HomeFixer360 template
        </p>
      </div>

      {/* Built-in Document Templates */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6" data-testid="builtin-templates-section">
        <h2 className="text-xl font-semibold text-gray-900 mb-1">Built-in Documents</h2>
        <p className="text-sm text-gray-500 mb-4">
          Ready-to-send templates pre-signed by your team. Each one populates with the
          customer's info and can be emailed straight from any estimate or lead.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Workmanship Warranty Card */}
          <div
            className="border border-gray-200 rounded-lg p-5 hover:border-blue-300 hover:shadow-md transition cursor-pointer bg-gradient-to-br from-blue-50 to-white"
            data-testid="warranty-template-card"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900">Workmanship Warranty</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  5-year coverage, pre-signed by Chris McIntyre
                </p>
              </div>
              <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">
                Active
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Auto-populates with customer name, address, email, and phone, then emails
              the signed PDF to the homeowner for their records.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviewWarranty}
                data-testid="warranty-preview-btn"
              >
                <Eye className="w-4 h-4 mr-1.5" />
                Preview
              </Button>
              <Button
                size="sm"
                onClick={() => setShowWarrantySendModal(true)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="warranty-quick-send-btn"
              >
                <Send className="w-4 h-4 mr-1.5" />
                Quick Send
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Current Template Status */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Active Contract Template</h2>
          {activeTemplate ? (
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              Custom Template
            </span>
          ) : (
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              Default Template
            </span>
          )}
        </div>

        {activeTemplate ? (
          <div className="space-y-3">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
              <span className="font-medium">{activeTemplate.template_name}</span>
            </div>
            {activeTemplate.description && (
              <p className="text-gray-600 text-sm">{activeTemplate.description}</p>
            )}
            <p className="text-sm text-gray-500">
              Uploaded: {new Date(activeTemplate.created_at).toLocaleDateString()}
            </p>
            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => window.open(getFilePreviewUrl(activeTemplate), '_blank')}
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                variant="outline"
                onClick={handleUseDefault}
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
              >
                Switch to Default
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <FileText className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600 mb-2">
              You're using the default HomeFixer360 contract template
            </p>
            <p className="text-sm text-gray-500">
              Upload your own template to customize contracts for your business
            </p>
          </div>
        )}
      </div>

      {/* Upload New Template */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Upload New Template</h2>
          <Button 
            onClick={() => setShowUploadForm(!showUploadForm)}
            variant={showUploadForm ? "outline" : "default"}
          >
            {showUploadForm ? 'Cancel' : 'Upload Template'}
          </Button>
        </div>

        {showUploadForm && (
          <div className="space-y-4 pt-4 border-t">
            <div>
              <Label htmlFor="templateName">Template Name *</Label>
              <Input
                id="templateName"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Standard Roofing Contract 2025"
              />
            </div>
            <div>
              <Label htmlFor="templateDesc">Description (optional)</Label>
              <Input
                id="templateDesc"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="e.g., Includes extended warranty terms"
              />
            </div>
            <div>
              <Label>Contract File (PDF or DOCX)</Label>
              <input
                type="file"
                ref={fileInputRef}
                accept=".pdf,.docx"
                onChange={handleFileUpload}
                className="hidden"
              />
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-600">Click to select file</p>
                <p className="text-sm text-gray-400">PDF or DOCX, max 10MB</p>
              </div>
            </div>
            {isUploading && (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Uploading...</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <h4 className="font-semibold text-amber-900 mb-2">Template Requirements</h4>
          <ul className="text-sm text-amber-800 space-y-1">
            <li>• Include a signature line for the customer</li>
            <li>• Leave space for project details (address, pricing, etc.)</li>
            <li>• Include your company name and contact information</li>
            <li>• Digital signatures will be added automatically via DocuSeal</li>
          </ul>
        </div>
      </div>

      {/* Template History */}
      {templates.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">All Templates</h2>
          <div className="space-y-3">
            {templates.map((template) => (
              <div 
                key={template.id} 
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  template.is_active 
                    ? 'border-blue-300 bg-blue-50' 
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-center">
                  <FileText className={`w-8 h-8 mr-3 ${template.is_active ? 'text-blue-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-medium text-gray-900">
                      {template.template_name}
                      {template.is_active && (
                        <span className="ml-2 text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">Active</span>
                      )}
                    </p>
                    <p className="text-sm text-gray-500">
                      {template.file_type?.toUpperCase()} • {new Date(template.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(getFilePreviewUrl(template), '_blank')}
                    title="Preview"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  {!template.is_active && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleActivate(template.id)}
                    >
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(template.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Workmanship Warranty Quick Send Modal */}
      {showWarrantySendModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="warranty-send-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Send Workmanship Warranty</h3>
                <p className="text-sm text-gray-500">
                  PDF will be pre-signed by Chris McIntyre and emailed to the customer.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="w-name">Customer Name *</Label>
                <Input
                  id="w-name"
                  value={warrantyForm.customer_name}
                  onChange={(e) => setWarrantyForm({ ...warrantyForm, customer_name: e.target.value })}
                  placeholder="Richard Barbee"
                  data-testid="warranty-name-input"
                />
              </div>
              <div>
                <Label htmlFor="w-email">Customer Email *</Label>
                <Input
                  id="w-email"
                  type="email"
                  value={warrantyForm.email}
                  onChange={(e) => setWarrantyForm({ ...warrantyForm, email: e.target.value })}
                  placeholder="customer@example.com"
                  data-testid="warranty-email-input"
                />
              </div>
              <div>
                <Label htmlFor="w-address">Property Address</Label>
                <Input
                  id="w-address"
                  value={warrantyForm.address}
                  onChange={(e) => setWarrantyForm({ ...warrantyForm, address: e.target.value })}
                  placeholder="4109 Stoneridge Drive, Durham, NC 27704"
                  data-testid="warranty-address-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="w-phone">Phone</Label>
                  <Input
                    id="w-phone"
                    value={warrantyForm.phone}
                    onChange={(e) => setWarrantyForm({ ...warrantyForm, phone: e.target.value })}
                    placeholder="919-555-1234"
                    data-testid="warranty-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="w-date">Completion Date</Label>
                  <Input
                    id="w-date"
                    value={warrantyForm.project_completion_date}
                    onChange={(e) => setWarrantyForm({ ...warrantyForm, project_completion_date: e.target.value })}
                    placeholder="May 1, 2026"
                    data-testid="warranty-date-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowWarrantySendModal(false)}
                disabled={isSendingWarranty}
                className="flex-1"
                data-testid="warranty-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={handlePreviewWarranty}
                disabled={isSendingWarranty}
                data-testid="warranty-modal-preview-btn"
              >
                <Eye className="w-4 h-4 mr-1.5" />
                Preview
              </Button>
              <Button
                onClick={handleSendWarranty}
                disabled={isSendingWarranty}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="warranty-send-btn"
              >
                {isSendingWarranty ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-1.5" />
                    Send Warranty
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractTemplates;
